import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { TopNav } from '../TopNav';
import { Sidebar } from '../Sidebar';
import { MeshBackground } from '../MeshBackground';
import { AppFooter } from '../AppFooter';
import { platformSidebarConfig } from '../nav/platformSidebarConfig';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/lib/api';
/**
 * Platform-mode shell — the default authenticated layout. Shown on every
 * route outside of `/products/:slug/*`. The sidebar renders
 * `platformSidebarConfig(user)` which adapts to the user's org type
 * (STANDALONE / MSP / MSP_CLIENT / RESELLER). At V1 only STANDALONE
 * renders, so MSP + Reseller sections collapse away.
 *
 * Accent colours fall back to the Chaosology royal/lav palette — ProductShell
 * is the one that swaps those out per product.
 */
export function PlatformShell() {
    const user = useAuthStore((s) => s.user);
    const sections = platformSidebarConfig(user);
    // Live counts patched onto the sidebar so badges reflect reality.
    // Keep this list small; large fan-out of background queries from the
    // shell would slow every page load.
    const { data: alertProviders } = useQuery({
        queryKey: ['alerts', 'providers'],
        queryFn: () => apiClient.get('/sslwidewatch/alerts/providers'),
        enabled: !!user,
        staleTime: 60_000,
    });
    const { data: alertGroups } = useQuery({
        queryKey: ['alerts', 'groups'],
        queryFn: () => apiClient.get('/sslwidewatch/alerts/groups'),
        enabled: !!user,
        staleTime: 60_000,
    });
    const alertProvidersCount = Array.isArray(alertProviders) ? alertProviders.length : null;
    const alertGroupsCount = Array.isArray(alertGroups) ? alertGroups.length : null;
    // Patch sidebar badges with live counts. We only attach a badge when
    // the count is known and > 0 — empty sections shouldn't pretend to
    // have content.
    const liveSections = sections.map((section) => ({
        ...section,
        items: section.items.map((item) => {
            if (item.to === '/alerts/providers' && alertProvidersCount !== null && alertProvidersCount > 0) {
                return { ...item, badge: { text: String(alertProvidersCount), kind: 'count' } };
            }
            if (item.to === '/alerts/groups' && alertGroupsCount !== null && alertGroupsCount > 0) {
                return { ...item, badge: { text: String(alertGroupsCount), kind: 'count' } };
            }
            return item;
        }),
    }));
    return (_jsxs(_Fragment, { children: [_jsx(MeshBackground, {}), _jsxs("div", { className: "relative z-[1] grid min-h-screen mx-auto max-w-[1680px]", style: {
                    gridTemplateColumns: '272px 1fr',
                    gridTemplateRows: '68px 1fr',
                    gridTemplateAreas: `"nav nav" "side main"`,
                }, children: [_jsx(TopNav, {}), _jsx(Sidebar, { sections: liveSections }), _jsxs("main", { className: "flex flex-col overflow-y-auto px-9 pt-6 pb-10", style: { gridArea: 'main' }, children: [_jsx("div", { className: "flex-1", children: _jsx(Outlet, {}) }), _jsx(AppFooter, {})] })] })] }));
}
